/**
 * Layout Persona Configuration
 *
 * Persona for viewing and editing the per-floor wall skeleton as an
 * abstracted schematic. Default 2D top-down view; the only tool is the
 * skeleton editor (reused from the floorplan refinement mode).
 *
 * On entry, the persona offers to bootstrap a skeleton on every floor that
 * already has an outline but no skeleton yet — see {@link LayoutPersona}.
 */

import { IPersonaConfig, ToolbarElementType } from '../../types';
import { ViewModes } from '../../helpers';

import { FloorSelectorTool, WallSketchEditorTool, OpeningSketchDrawTool, SketchObjectTool, CameraOrientationTool } from '../../tools';
import { LayoutPersona } from '../../personas/implementations/LayoutPersona';

export const LayoutPersonaConfig: IPersonaConfig = {
  name: 'layout',
  priority: 85,
  exclusive: false,
  factory: (viewer, config, viewManager) => new LayoutPersona(viewer, config, viewManager),

  defaultViewConfig: {
    views: [ViewModes.ORTHOGRAPHIC_2D_TOP, ViewModes.PERSPECTIVE_3D_RGB],
    defaultViewId: ViewModes.ORTHOGRAPHIC_2D_TOP.id,
  },

  modes: [
    {
      id: 'edit',
      name: 'edit',
      toolbarElements: [
        // Default selection tool — the floor selector (same tool the floorplan
        // persona uses): visualizes the detected floor planes and click-selects a
        // floor. Always available (no floor prerequisite).
        {
          type: ToolbarElementType.TOOL,
          config: {
            // id matches the floor persona's so FloorToolBase's internal
            // `enableTool('floor_selector')` (Escape → back to selector) resolves.
            id: 'floor_selector',
            factory: (viewer, persona) => new FloorSelectorTool(viewer, persona),
            defaultEnabled: true,
            exclusive: true,
            icon: 'select',
            name: 'select',
          },
        },
        // Align the 2D view with the floor's walls (same tool the floorplan
        // persona uses): click two points to define a horizontal line, or
        // right-click to auto-detect the dominant wall orientation.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'camera_orientation',
            factory: (viewer, persona) => new CameraOrientationTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'undo-dot',
            name: 'camera orientation',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'layout_editor',
            factory: (viewer, persona) => new WallSketchEditorTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true,
            exclusive: true,
            icon: 'Room-detection-manueel',
            name: 'editor',
            disabledTooltip: 'select a floor first',
          },
        },
        // Draw / move door & window openings on the wall sketch. Door vs window
        // is a tool setting (rendered by the shared settings panel).
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'opening_sketch_draw',
            factory: (viewer, persona) => new OpeningSketchDrawTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true,
            exclusive: true,
            icon: 'door',
            name: 'opening',
            disabledTooltip: 'select a floor first',
          },
        },
        // Place / edit sketch objects (furniture & fixtures) from the type
        // library, which lives in the tool-settings menu (auto-opened).
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'sketch_object',
            factory: (viewer, persona) => new SketchObjectTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true,
            exclusive: true,
            // Chair: rectangular, so it reads as a placeable object at toolbar size.
            icon: 'chair',
            name: 'objects',
            disabledTooltip: 'select a floor first',
          },
        },

        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        {
          type: ToolbarElementType.ACTION,
          id: 'undo',
          icon: 'undo',
          tooltip: 'undo',
        },
        {
          type: ToolbarElementType.ACTION,
          id: 'redo',
          icon: 'redo',
          tooltip: 'redo',
        },
      ],
      viewConfig: {
        views: [ViewModes.XRAY_2D, ViewModes.PERSPECTIVE_3D_RGB],
        defaultViewId: ViewModes.XRAY_2D.id,
      },
    },
  ],
};
